import { View, Text, Card, Checkbox } from "@src/components";
import { Icon } from "@src/components/Icon";
import { PressableScale } from "pressto";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { EmailSuggestion, EmailContact } from "../types";

type EmailSuggestionsListProps = {
  suggestions: EmailSuggestion[];
  existingContacts: EmailContact[];
  searchQuery: string;
  selectedEmails: Set<string>;
  onToggleEmail: (email: string, checked: boolean) => void;
  onCreateContact: () => void;
};

export function EmailSuggestionsList({
  suggestions,
  existingContacts,
  searchQuery,
  selectedEmails,
  onToggleEmail,
  onCreateContact,
}: EmailSuggestionsListProps) {
  const { theme } = useUnistyles();
  const query = searchQuery.trim().toLowerCase();
  const existingEmails = new Set(existingContacts.map((c) => c.email.toLowerCase()));

  const availableSuggestions = suggestions
    .filter((s) => !existingEmails.has(s.email.toLowerCase()))
    .filter((s) => !query || s.email.toLowerCase().includes(query) || (s.name && s.name.toLowerCase().includes(query)));

  return (
    <>
      {query ?
        <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="xl">
          <Text textStyle="xs/regular" color="textMuted">
            Results for &quot;{query}&quot;
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            {availableSuggestions.length} {availableSuggestions.length === 1 ? "result" : "results"}
          </Text>
        </View>
      : null}
      {availableSuggestions.length > 0 && (
        <Card style={styles.card}>
          {availableSuggestions.map((suggestion, index) => {
            const isSelected = selectedEmails.has(suggestion.email);
            return (
              <PressableScale key={suggestion.email} onPress={() => onToggleEmail(suggestion.email, !isSelected)}>
                <View
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  py="xl"
                  px="xl"
                  style={[styles.suggestionItem, index === availableSuggestions.length - 1 && styles.lastItem]}
                >
                  <View flex={1} mr="xl">
                    {suggestion.name ?
                      <>
                        <Text textStyle="sm/regular" numberOfLines={1}>
                          {suggestion.name}
                        </Text>
                        <Text textStyle="xs/regular" color="textMuted" numberOfLines={1} mt="xs">
                          {suggestion.email}
                        </Text>
                      </>
                    : <Text textStyle="sm/regular" numberOfLines={1}>
                        {suggestion.email}
                      </Text>
                    }
                  </View>
                  <Checkbox checked={isSelected} onToggle={(checked) => onToggleEmail(suggestion.email, checked)} />
                </View>
              </PressableScale>
            );
          })}
        </Card>
      )}
      <PressableScale onPress={onCreateContact}>
        <View style={styles.button}>
          <Text textStyle="sm/regular">Create new contact</Text>
          <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
        </View>
      </PressableScale>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  card: {
    paddingVertical: 0,
    paddingHorizontal: 0,
    borderRadius: theme.spacing.md,
    backgroundColor: theme.colors.surfaceCard,
  },
  suggestionItem: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  lastItem: {
    borderBottomWidth: 0,
  },
  button: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.secondary.default_border,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
    marginTop: theme.spacing["3xl"],
  },
}));
